import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AIContentService } from '../../../services/ai-content.service';
import { PreviewV2Service } from '../../../services/v2/preview-v2.service';
import { ToastrService } from 'ngx-toastr';

export interface ContentEditRequestV2 {
  tagName: string;
  currentContent: string;
  section: string; // The section this edit belongs to (capabilities, about-us, etc.)
}

@Component({
  selector: 'app-inline-content-editor-v2',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './inline-content-editor-v2.component.html',
  styleUrls: ['./inline-content-editor-v2.component.scss']
})
export class InlineContentEditorV2Component implements OnInit, OnDestroy, OnChanges {
  @Input() editRequest: ContentEditRequestV2 | null = null;
  @Input() sectionTheme: string = 'default'; // Section-specific styling theme
  @Input() currentSupplierData: any = {};
  
  @Output() contentUpdated = new EventEmitter<{ tagName: string; newContent: string }>();
  @Output() editCancelled = new EventEmitter<void>();

  @ViewChild('contentTextarea') contentTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('feedbackTextarea') feedbackTextarea!: ElementRef<HTMLTextAreaElement>;

  // Content state
  originalContent: string = '';
  newContent: string = '';
  userFeedback: string = '';
  isGenerating: boolean = false;
  showAcceptReject: boolean = false;
  
  // Prevent unnecessary re-initialization
  private lastProcessedRequest: ContentEditRequestV2 | null = null;

  constructor(
    private aiContentService: AIContentService,
    private previewService: PreviewV2Service,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.editRequest) {
      this.initializeEditor(this.editRequest);
    }
  }

  ngOnChanges(): void {
    if (this.editRequest && this.editRequest !== this.lastProcessedRequest) {
      console.log('V2 Content Editor: Processing new edit request:', this.editRequest);
      this.lastProcessedRequest = this.editRequest;
      this.initializeEditor(this.editRequest);
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private initializeEditor(request: ContentEditRequestV2): void {
    console.log(`V2 Editor: Initializing for section ${request.section}, tag ${request.tagName}`);
    
    this.originalContent = request.currentContent || '';
    this.newContent = request.currentContent || '';
    this.userFeedback = '';
    this.showAcceptReject = false;
    this.isGenerating = false;

    // Focus on content textarea after a short delay
    setTimeout(() => {
      if (this.contentTextarea?.nativeElement) {
        this.contentTextarea.nativeElement.focus();
        // Position cursor at end
        const element = this.contentTextarea.nativeElement;
        element.setSelectionRange(element.value.length, element.value.length);
      }
    }, 100);
  }

  // Prevent focus conflicts by handling content field events
  onContentFocus(): void {
    console.log('V2 Editor: Content field focused');
  }

  onContentClick(event: Event): void {
    // Allow normal content field interaction
    console.log('V2 Editor: Content field clicked');
  }

  async regenerateContent(): Promise<void> {
    if (!this.editRequest || !this.userFeedback.trim()) {
      return;
    }

    this.isGenerating = true;

    try {
      console.log(`V2 Editor: Regenerating content for tag ${this.editRequest.tagName}`);
      
      // Use the AI service to regenerate content
      const tagReplacements = await this.aiContentService.regenerateSingleTag(
        this.editRequest.tagName,
        this.originalContent,
        this.userFeedback,
        this.currentSupplierData
      );

      if (tagReplacements.length > 0 && tagReplacements[0].tagName === this.editRequest.tagName) {
        this.newContent = tagReplacements[0].replacementHTML;
        this.showAcceptReject = true;
        console.log('V2 Editor: Content regenerated successfully');
      } else {
        throw new Error('No content generated for the specified tag');
      }

    } catch (error) {
      console.error('V2 Editor: Content regeneration failed:', error);
      this.toastr.error(`Failed to regenerate content`, 'AI Processing Error');
    } finally {
      this.isGenerating = false;
    }
  }

  acceptGeneratedContent(): void {
    if (this.newContent.trim()) {
      // Accept the AI-generated content
      this.originalContent = this.newContent.trim();
      this.showAcceptReject = false;
      this.userFeedback = ''; // Clear feedback after accepting
      console.log('V2 Editor: AI content accepted');
      
      // ✅ NEW: Automatically save the accepted content and close editor
      this.saveContent();
    }
  }

  rejectGeneratedContent(): void {
    // Reject and revert to original content
    this.newContent = this.originalContent;
    this.showAcceptReject = false;
    this.userFeedback = ''; // Clear feedback after rejecting
    console.log('V2 Editor: AI content rejected');
  }

  async saveContent(): Promise<void> {
    if (!this.editRequest) return;
    
    try {
      console.log('V2 Content Editor: Saving content for tag:', this.editRequest.tagName);
      
      // Update in IndexedDB (existing functionality)
      this.contentUpdated.emit({
        tagName: this.editRequest.tagName,
        newContent: this.newContent
      });
      
      // Update in dict.js (new functionality)
      await this.previewService.updateSingleTagInDict(
        this.editRequest.tagName, 
        this.newContent
      );
      
      this.editCancelled.emit();
    } catch (error) {
      console.error('V2 Content Editor: Failed to save content:', error);
    }
  }

  cancelEdit(): void {
    console.log('V2 Editor: Edit cancelled');
    this.editCancelled.emit();
  }

  // Real-time content update (for live preview)
  onContentChange(): void {
    // Emit real-time updates for preview
    if (this.editRequest && this.newContent !== this.originalContent) {
      // Could emit real-time updates here if needed
      // For now, we'll wait for explicit save
    }
  }

  // Handle AI feedback field focus
  onFeedbackFocus(): void {
    // Ensure the feedback field maintains focus
    console.log('V2 Editor: AI feedback field focused');
  }

  // Handle AI feedback field click
  onFeedbackClick(event: Event): void {
    // Prevent any focus conflicts
    event.stopPropagation();
    console.log('V2 Editor: AI feedback field clicked');
  }

  // Get section-specific labels and placeholders
  getSectionDisplayName(): string {
    const sectionNames: { [key: string]: string } = {
      'capabilities': 'Capabilities',
      'about-us': 'About Us',
      'contact': 'Contact',
      'template': 'Home'
    };
    return sectionNames[this.sectionTheme] || 'Content';
  }

  getContentLabel(): string {
    return `Edit ${this.getSectionDisplayName()} Content`;
  }

  getFeedbackPlaceholder(): string {
    return `Describe how you'd like to improve this ${this.getSectionDisplayName().toLowerCase()} content...`;
  }
} 